import { useState, useEffect, useCallback } from 'react';
import { Bug, Lightbulb, Frown, ChevronDown, ChevronUp, LifeBuoy, MessageSquarePlus, Mail, ChevronRight, ArrowLeft, Landmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/utils/api';
import { isLexContext } from '@/utils/lexDomain';
import { useAuth } from '@/contexts/AuthContext';

const FloatingFeedbackButton = () => {
  const { user } = useAuth();
  // lex.ivoliq: Navy/Gold + Sie-Form (Styleguide /app/design_guidelines_lex.json), sonst ivoliq Emerald + Du-Form
  const lex = isLexContext(user);

  const [isOpen, setIsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('other');
  const [showDetails, setShowDetails] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackEnabled, setFeedbackEnabled] = useState(false);
  const [consentToContact, setConsentToContact] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  // Support-Dialog State
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportSubject, setSupportSubject] = useState('');
  const [supportMessage, setSupportMessage] = useState('');
  const [supportConsent, setSupportConsent] = useState(false);
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  // lex: Direkter Draht zur betreuenden Kanzlei (Juli 2026)
  const [kanzleiOpen, setKanzleiOpen] = useState(false);

  // lex-Dashboard (Sep 2026): "Nachricht an die Kanzlei" öffnet den Kontakt-Dialog per Event
  useEffect(() => {
    const open = () => setKanzleiOpen(true);
    window.addEventListener('lex-open-kanzlei-contact', open);
    return () => window.removeEventListener('lex-open-kanzlei-contact', open);
  }, []);
  const [kanzleiSubject, setKanzleiSubject] = useState('');
  const [kanzleiMessage, setKanzleiMessage] = useState('');
  const [kanzleiSubmitting, setKanzleiSubmitting] = useState(false);
  // P3 z-index-Fix (Feb 2026): dimmen wenn Closure-Wizard offen.
  const [dimmed, setDimmed] = useState(false);
  useEffect(() => {
    const onOpen = () => setDimmed(true);
    const onCloseEv = () => setDimmed(false);
    window.addEventListener('ivoliq:wizard-open', onOpen);
    window.addEventListener('ivoliq:wizard-close', onCloseEv);
    return () => {
      window.removeEventListener('ivoliq:wizard-open', onOpen);
      window.removeEventListener('ivoliq:wizard-close', onCloseEv);
    };
  }, []);

  // Theme: eine Quelle für beide Produkte
  const T = lex
    ? {
        radius: 'rounded-none',
        itemRadius: 'rounded-none',
        iconWrap: 'rounded-none bg-[#FBF7EF] text-[#8a6d33]',
        iconWrapHover: 'group-hover/item:bg-[#0A1128] group-hover/item:text-[#C5A059]',
        itemHover: 'hover:bg-[#FBF7EF] focus:bg-[#FBF7EF]',
        cardHover: 'hover:border-[#C5A059] hover:bg-[#FBF7EF]',
        chevronHover: 'group-hover/item:text-[#C5A059]',
        pill: 'bg-[#0A1128] hover:bg-black focus:bg-black shadow-lg shadow-[#0A1128]/20 focus:ring-[#C5A059]',
        primaryBtn: 'bg-[#0A1128] hover:bg-black text-white rounded-none',
        infoBox: 'bg-[#FBF7EF] border border-[#e6d9bd]',
        infoIcon: 'text-[#8a6d33]',
        link: 'text-[#8a6d33] hover:text-[#0A1128]',
        checkbox: 'accent-[#C5A059]',
        inputFocus: 'focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]',
        brand: 'lex.ivoliq',
      }
    : {
        radius: 'rounded-2xl',
        itemRadius: 'rounded-xl',
        iconWrap: 'rounded-full bg-zinc-100 text-zinc-600',
        iconWrapHover: 'group-hover/item:bg-zinc-900 group-hover/item:text-white',
        itemHover: 'hover:bg-zinc-50 focus:bg-zinc-50',
        cardHover: 'hover:border-emerald-500 hover:bg-emerald-50/40',
        chevronHover: 'group-hover/item:text-emerald-500',
        pill: 'bg-emerald-600 hover:bg-emerald-700 focus:bg-emerald-700 shadow-lg shadow-emerald-600/20 focus:ring-emerald-500',
        primaryBtn: '',
        infoBox: 'bg-emerald-50 border border-emerald-100',
        infoIcon: 'text-emerald-600',
        link: 'text-emerald-600 hover:text-emerald-700',
        checkbox: 'accent-emerald-600',
        brand: 'ivoliq',
        inputFocus: 'focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500',
      };

  // Wording: lex = Sie-Form, ivoliq = Du-Form
  const W = lex
    ? {
        menuTitle: 'Wie können wir helfen?',
        menuSub: 'Wir sind aktiv dran und melden uns schnell.',
        supportSub: 'Technische Fragen zur Plattform',
        feedbackSub: 'Bug, Idee oder UX-Problem melden',
        fbTitle: 'Feedback geben',
        fbSub: 'Was können wir besser machen? Wir lesen jede Nachricht.',
        descPlaceholder: 'Beschreiben Sie Ihr Anliegen so genau wie möglich (mind. 10 Zeichen)…',
        descHint: 'Je genauer die Beschreibung, desto schneller können wir helfen.',
        consentLabel: 'Ja, lex.ivoliq darf mich per E-Mail zu diesem Feedback kontaktieren',
        supportReplyTo: 'Wir antworten an Ihre registrierte E-Mail:',
        supportMsgLabel: 'Ihr Anliegen *',
        supportPlaceholder: 'Beschreiben Sie Ihr Anliegen so genau wie möglich (mind. 10 Zeichen)…',
      }
    : {
        menuTitle: 'Wie können wir helfen?',
        menuSub: 'Wir sind aktiv dran und melden uns schnell.',
        supportSub: 'Schreib uns – wir antworten per E-Mail',
        feedbackSub: 'Bug, Idee oder UX-Problem melden',
        fbTitle: 'Feedback geben',
        fbSub: 'Was können wir besser machen? Wir lesen jede Nachricht.',
        descPlaceholder: 'Beschreibe dein Anliegen so genau wie möglich (mind. 10 Zeichen)…',
        descHint: 'Je genauer die Beschreibung, desto schneller können wir helfen.',
        consentLabel: 'Ja, ivoliq darf mich per E-Mail zu diesem Feedback kontaktieren',
        supportReplyTo: 'Wir antworten an deine registrierte E-Mail:',
        supportMsgLabel: 'Dein Anliegen *',
        supportPlaceholder: 'Beschreibe dein Anliegen so genau wie möglich (mind. 10 Zeichen)…',
      };

  const resetForm = useCallback(() => {
    setSelectedType(null);
    setDescription('');
    setCategory('other');
    setShowDetails(false);
    setConsentToContact(false);
    setValidationErrors({});
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    resetForm();
  }, [resetForm]);

  // Öffnet den Feedback-Dialog aus dem Launcher-Menü
  const openFeedbackDialog = useCallback(() => {
    setMenuOpen(false);
    setIsOpen(true);
  }, []);

  const resetSupportForm = useCallback(() => {
    setSupportSubject('');
    setSupportMessage('');
    setSupportConsent(false);
  }, []);

  // Öffnet den Support-Dialog (E-Mail-Versand via Backend/Brevo an support@ivoliq.io)
  const openSupportDialog = useCallback(() => {
    setMenuOpen(false);
    resetSupportForm();
    setSupportOpen(true);
    // Registrierte E-Mail laden, damit der User sieht, an welche Adresse wir antworten
    api.get('/auth/me')
      .then((res) => setUserEmail(res?.data?.email || ''))
      .catch(() => setUserEmail(''));
  }, [resetSupportForm]);

  const handleSupportClose = useCallback(() => {
    setSupportOpen(false);
    resetSupportForm();
  }, [resetSupportForm]);

  // lex: Kanzlei-Dialog öffnen/schließen/senden
  const openKanzleiDialog = useCallback(() => {
    setMenuOpen(false);
    setKanzleiSubject('');
    setKanzleiMessage('');
    setKanzleiOpen(true);
    api.get('/auth/me')
      .then((res) => setUserEmail(res?.data?.email || ''))
      .catch(() => setUserEmail(''));
  }, []);

  const handleKanzleiClose = useCallback(() => {
    setKanzleiOpen(false);
    setKanzleiSubject('');
    setKanzleiMessage('');
  }, []);

  const handleKanzleiSubmit = useCallback(async () => {
    if (kanzleiMessage.trim().length < 10) {
      toast.error('Bitte beschreiben Sie Ihr Anliegen (mind. 10 Zeichen).');
      return;
    }
    setKanzleiSubmitting(true);
    try {
      const res = await api.post('/lex/kanzlei-contact', {
        subject: kanzleiSubject.trim() || undefined,
        message: kanzleiMessage.trim(),
      });
      if (res?.data?.ok) {
        toast.success(
          <div>
            <div className="font-medium">Nachricht an Ihre Steuerberatung gesendet.</div>
            <div className="text-sm text-gray-600 mt-1">Referenz: {res.data.ticket_number} · Antwort per E-Mail.</div>
          </div>,
          { duration: 6000 }
        );
        handleKanzleiClose();
      } else {
        toast.error('Nachricht konnte nicht gesendet werden. Bitte erneut versuchen.');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Nachricht konnte nicht gesendet werden. Bitte erneut versuchen.');
    } finally {
      setKanzleiSubmitting(false);
    }
  }, [kanzleiMessage, kanzleiSubject, handleKanzleiClose]);

  const handleSupportSubmit = useCallback(async () => {
    if (supportMessage.trim().length < 10) {
      toast.error('Bitte beschreibe dein Anliegen (mind. 10 Zeichen).');
      return;
    }
    if (!supportConsent) {
      toast.error('Bitte bestätige, dass wir dich kontaktieren dürfen.');
      return;
    }
    setSupportSubmitting(true);
    try {
      const res = await api.post('/support/contact', {
        subject: supportSubject.trim() || undefined,
        message: supportMessage.trim(),
        consent_to_contact: true,
        page_url: window.location.href,
        browser_info: navigator.userAgent,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      });
      if (res?.data?.ok) {
        toast.success('Support-Anfrage gesendet! Wir melden uns per E-Mail.');
        handleSupportClose();
      } else {
        toast.error('Anfrage konnte nicht gesendet werden. Bitte erneut versuchen.');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Anfrage konnte nicht gesendet werden. Bitte erneut versuchen.');
    } finally {
      setSupportSubmitting(false);
    }
  }, [supportMessage, supportConsent, supportSubject, handleSupportClose]);

  // Check if feedback feature is enabled.
  // Default ON everywhere; only an explicit "false" disables it (so DEV + PROD beide zeigen die Pill).
  useEffect(() => {
    const flag = process.env.REACT_APP_FEEDBACK_ENABLED;
    setFeedbackEnabled(flag !== 'false');
  }, []);

  // Handle Escape key to close modal / menu
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      if (isOpen) handleClose();
      if (menuOpen) setMenuOpen(false);
    };

    if (isOpen || menuOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, menuOpen, handleClose]);

  // Don't render if feature is disabled
  if (!feedbackEnabled) {
    return null;
  }

  const feedbackTypes = [
    {
      id: 'negative',
      title: 'Bug melden',
      subtitle: 'Etwas funktioniert nicht',
      icon: Bug,
      iconTint: lex ? '' : 'bg-red-50 text-red-600',
    },
    {
      id: 'idea',
      title: 'Feature vorschlagen',
      subtitle: 'Idee für eine Verbesserung',
      icon: Lightbulb,
      iconTint: lex ? '' : 'bg-amber-50 text-amber-600',
    },
    {
      id: 'positive',
      title: 'UX-Problem',
      subtitle: 'Etwas ist verwirrend',
      icon: Frown,
      iconTint: lex ? '' : 'bg-zinc-100 text-zinc-600',
    },
  ];

  const categories = [
    { value: 'ui', label: 'UI/Design' },
    { value: 'feature', label: 'Feature' },
    { value: 'bug', label: 'Bug' },
    { value: 'performance', label: 'Performance' },
    { value: 'other', label: 'Sonstiges' }
  ];

  const detectModuleFromUrl = () => {
    const path = window.location.pathname;
    const routeMap = {
      '/invoices': 'Rechnungsmodul',
      '/customers': 'Kundenmodul',
      '/projects': 'Projektmodul',
      '/taxes': 'Steuermodul',
      '/settings': 'Einstellungen',
      '/dashboard': 'Dashboard',
      '/time-tracking': 'Zeiterfassung',
      '/lex': 'lex.ivoliq'
    };
    
    const match = Object.keys(routeMap).find(route => path.startsWith(route));
    return match ? routeMap[match] : 'Allgemein';
  };

  const validateDescription = (text) => {
    const trimmed = text.trim();
    if (trimmed.length === 0) {
      return 'Bitte eine Beschreibung angeben.';
    }
    if (trimmed.length < 10) {
      return 'Beschreibung zu kurz (mind. 10 Zeichen).';
    }
    if (trimmed.length > 500) {
      return 'Beschreibung zu lang (max. 500 Zeichen).';
    }
    return null;
  };

  const isFormValid = () => {
    return selectedType && validateDescription(description) === null;
  };

  const collectBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    // Extract browser name and version (simplified)
    let browserInfo = 'Unbekannt';
    if (userAgent.includes('Chrome')) {
      const match = userAgent.match(/Chrome\/(\d+)/);
      browserInfo = match ? `Chrome/${match[1]}` : 'Chrome';
    } else if (userAgent.includes('Firefox')) {
      const match = userAgent.match(/Firefox\/(\d+)/);
      browserInfo = match ? `Firefox/${match[1]}` : 'Firefox';
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      const match = userAgent.match(/Version\/(\d+)/);
      browserInfo = match ? `Safari/${match[1]}` : 'Safari';
    }
    return browserInfo;
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    // Auto-set category based on type
    if (type === 'negative') setCategory('bug');
    else if (type === 'idea') setCategory('feature');
    else if (type === 'positive') setCategory('ui');
  };

  const handleSubmit = async () => {
    if (!selectedType) {
      toast.error('Bitte einen Feedback-Typ auswählen.');
      return;
    }

    const descriptionError = validateDescription(description);
    if (descriptionError) {
      setValidationErrors({ description: descriptionError });
      toast.error(descriptionError);
      return;
    }

    setValidationErrors({});
    setIsSubmitting(true);

    try {
      const feedbackData = {
        feedback_type: selectedType,
        description: description.trim(),
        category: category,
        page_url: window.location.href,
        browser_info: collectBrowserInfo(),
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
        consent_to_contact: consentToContact
      };

      const response = await api.post('/feedback', feedbackData);
      
      if (response.data.success) {
        const successMessage = consentToContact 
          ? 'Feedback gesendet! Wir melden uns bei Rückfragen per E-Mail.'
          : 'Feedback gesendet! Vielen Dank für die Hilfe.';
        
        toast.success(
          <div>
            <div className="font-medium">{successMessage}</div>
            <div className="text-sm text-gray-600 mt-1">
              Referenz: {response.data.feedback_id}
            </div>
          </div>,
          {
            duration: 5000,
            action: {
              label: 'Weiteres Feedback',
              onClick: () => {
                resetForm();
                setIsOpen(true);
              }
            }
          }
        );
        
        // Close modal and reset form
        setIsOpen(false);
        resetForm();
      } else {
        toast.error(response.data.message || 'Feedback konnte nicht gesendet werden.');
      }
    } catch (error) {
      console.error('Feedback submission error:', error);
      
      if (error.response?.status === 429) {
        toast.error('Zu viele Anfragen. Bitte kurz warten.');
      } else if (error.response?.status === 503) {
        toast.error('Feedback-System ist derzeit nicht verfügbar.');
      } else {
        toast.error('Feedback konnte nicht gesendet werden. Bitte erneut versuchen.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const characterCount = description.length;
  const maxCharacters = 500;
  const selected = feedbackTypes.find((t) => t.id === selectedType);

  return (
    <>
      {/* Floating Launcher: Feedback & Support */}
      <div
        className={`fixed bottom-6 right-6 z-50 ${dimmed ? 'opacity-20 pointer-events-none' : ''}`}
      >
        {/* Popover-Menü */}
        {menuOpen && (
          <>
            {/* Backdrop für Outside-Click */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setMenuOpen(false)}
              data-testid="feedback-support-backdrop"
              aria-hidden="true"
            />
            <div
              className={`absolute bottom-[4.5rem] right-0 z-50 w-[19rem] origin-bottom-right ${T.radius} border border-zinc-200 bg-white shadow-2xl shadow-zinc-900/10 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200`}
              data-testid="feedback-support-menu"
              role="menu"
            >
              <div className="px-4 pt-4 pb-2">
                <p className="font-bold text-zinc-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {W.menuTitle}
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {W.menuSub}
                </p>
              </div>

              <div className="p-2 pt-1 space-y-1">
                {/* lex: Direkter Draht zur Kanzlei — steuerliche/inhaltliche Fragen */}
                {lex && (
                  <button
                    onClick={openKanzleiDialog}
                    data-testid="feedback-support-menu-kanzlei"
                    role="menuitem"
                    className={`group/item w-full flex items-center gap-3 ${T.itemRadius} p-3 text-left transition-colors ${T.itemHover} focus:outline-none`}
                  >
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center ${T.iconWrap} ${T.iconWrapHover} transition-colors`}>
                      <Landmark className="h-4 w-4" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-zinc-900">Meine Steuerberatung</span>
                      <span className="block text-xs text-zinc-500 truncate">Direkter Draht zu Ihrer Kanzlei</span>
                    </span>
                    <ChevronRight className={`h-4 w-4 text-zinc-300 ${T.chevronHover} transition-colors`} />
                  </button>
                )}

                {/* Support */}
                <button
                  onClick={openSupportDialog}
                  data-testid="feedback-support-menu-support"
                  role="menuitem"
                  className={`group/item w-full flex items-center gap-3 ${T.itemRadius} p-3 text-left transition-colors ${T.itemHover} focus:outline-none`}
                >
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center ${T.iconWrap} ${T.iconWrapHover} transition-colors`}>
                    <Mail className="h-4 w-4" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-zinc-900">Support kontaktieren</span>
                    <span className="block text-xs text-zinc-500 truncate">{W.supportSub}</span>
                  </span>
                  <ChevronRight className={`h-4 w-4 text-zinc-300 ${T.chevronHover} transition-colors`} />
                </button>

                {/* Feedback */}
                <button
                  onClick={openFeedbackDialog}
                  data-testid="feedback-support-menu-feedback"
                  role="menuitem"
                  className={`group/item w-full flex items-center gap-3 ${T.itemRadius} p-3 text-left transition-colors ${T.itemHover} focus:outline-none`}
                >
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center ${T.iconWrap} ${T.iconWrapHover} transition-colors`}>
                    <MessageSquarePlus className="h-4 w-4" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-zinc-900">Feedback geben</span>
                    <span className="block text-xs text-zinc-500 truncate">{W.feedbackSub}</span>
                  </span>
                  <ChevronRight className={`h-4 w-4 text-zinc-300 ${T.chevronHover} transition-colors`} />
                </button>
              </div>
            </div>
          </>
        )}

        {/* Pill-Button */}
        <button
          onClick={() => setMenuOpen((o) => !o)}
          data-testid="feedback-support-pill"
          className={`group fixed bottom-6 right-6 ${T.pill} text-white hover:shadow-xl transition-all duration-300 z-50 focus:outline-none focus:ring-2 focus:ring-offset-2
                     /* Mobile: Always show as pill with text */
                     rounded-full px-5 py-4 flex items-center justify-center gap-2.5
                     /* Desktop: Start as circle, expand to pill on hover/focus */
                     md:w-14 md:h-14 md:p-0 md:gap-0 md:hover:w-auto md:hover:px-5 md:hover:py-4 md:hover:gap-2.5 md:hover:justify-start md:focus:w-auto md:focus:px-5 md:focus:py-4 md:focus:gap-2.5 md:focus:justify-start
                     /* Touch devices: Always show text */
                     [@media(hover:none)]:px-5 [@media(hover:none)]:py-4 [@media(hover:none)]:w-auto [@media(hover:none)]:justify-start [@media(hover:none)]:gap-2.5`}
          title="Feedback & Support"
          aria-label="Feedback & Support"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <LifeBuoy className="h-6 w-6 flex-shrink-0" />
          <span className="text-sm font-medium whitespace-nowrap
                          block
                          md:opacity-0 md:w-0 md:overflow-hidden md:group-hover:opacity-100 md:group-hover:w-auto md:group-focus:opacity-100 md:group-focus:w-auto md:transition-all md:duration-300
                          [@media(hover:none)]:opacity-100 [@media(hover:none)]:w-auto">
            Hilfe & Feedback
          </span>
        </button>
      </div>

      {/* Feedback Modal — Redesign Juli 2026: einheitlich mit Launcher-Menü & Support-Dialog */}
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className={`sm:max-w-md ${T.radius}`} data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="tracking-tight font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {W.fbTitle}
            </DialogTitle>
            <p className="text-sm text-zinc-500">{W.fbSub}</p>
          </DialogHeader>

          {!selectedType ? (
            // Schritt 1: Typ wählen — gleiche Optik wie die Launcher-Menü-Einträge
            <div className="space-y-2" data-testid="feedback-type-list">
              {feedbackTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => handleTypeSelect(type.id)}
                    data-testid={`feedback-type-${type.id}`}
                    className={`group/item w-full flex items-center gap-3 ${T.itemRadius} border border-zinc-200 p-3.5 text-left transition-colors ${T.cardHover} focus:outline-none`}
                  >
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center ${type.iconTint || T.iconWrap} ${lex ? '' : 'rounded-full'} ${lex ? T.iconWrapHover : ''} transition-colors`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-zinc-900">{type.title}</span>
                      <span className="block text-xs text-zinc-500 truncate">{type.subtitle}</span>
                    </span>
                    <ChevronRight className={`h-4 w-4 text-zinc-300 ${T.chevronHover} transition-colors`} />
                  </button>
                );
              })}
            </div>
          ) : (
            // Schritt 2: Details
            <div className="space-y-4">
              {/* Gewählter Typ als Chip + Zurück */}
              <div className={`flex items-center justify-between ${T.itemRadius} border border-zinc-200 bg-zinc-50/60 px-3 py-2.5`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center ${selected?.iconTint || T.iconWrap} ${lex ? '' : 'rounded-full'}`}>
                    {selected && <selected.icon className="h-3.5 w-3.5" />}
                  </span>
                  <span className="text-sm font-semibold text-zinc-900 truncate">{selected?.title}</span>
                </div>
                <button
                  onClick={() => setSelectedType(null)}
                  data-testid="feedback-change-type"
                  className={`inline-flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-900 transition-colors`}
                >
                  <ArrowLeft className="h-3 w-3" /> Ändern
                </button>
              </div>

              {/* Beschreibung */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-zinc-700">Beschreibung *</label>
                <Textarea
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    if (validationErrors.description) {
                      setValidationErrors(prev => ({ ...prev, description: null }));
                    }
                  }}
                  placeholder={W.descPlaceholder}
                  data-testid="feedback-description-input"
                  className={`min-h-[100px] resize-none ${T.itemRadius} ${
                    validationErrors.description ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  maxLength={maxCharacters}
                />
                {validationErrors.description && (
                  <div className="text-sm text-red-600">{validationErrors.description}</div>
                )}
                <div className="flex justify-between text-xs text-zinc-400">
                  <span>{W.descHint}</span>
                  <span className={characterCount > 0 && characterCount < 10 ? 'text-amber-600' : ''}>
                    {characterCount}/500
                  </span>
                </div>
              </div>

              {/* Consent */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="consent-checkbox"
                  checked={consentToContact}
                  onChange={(e) => setConsentToContact(e.target.checked)}
                  data-testid="feedback-consent-checkbox"
                  className={`mt-1 h-4 w-4 border-zinc-300 rounded ${T.checkbox}`}
                />
                <label htmlFor="consent-checkbox" className="text-sm text-zinc-600 cursor-pointer">
                  {W.consentLabel}{' '}
                  <span className="text-xs text-zinc-400">
                    (optional ·{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className={`${T.link} underline`}>
                      Datenschutz
                    </a>)
                  </span>
                </label>
              </div>

              {/* Mehr Details */}
              <div className={`border border-zinc-200 ${T.itemRadius} overflow-hidden`}>
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  data-testid="feedback-details-toggle"
                  className={`w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors ${T.itemHover}`}
                >
                  <span className="text-sm font-medium text-zinc-700">Mehr Details</span>
                  {showDetails ? (
                    <ChevronUp className="h-4 w-4 text-zinc-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-zinc-400" />
                  )}
                </button>

                {showDetails && (
                  <div className="px-3 pb-3 pt-2 space-y-3 border-t border-zinc-100">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-zinc-700">Kategorie</label>
                      <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger className={T.itemRadius}>
                          <SelectValue placeholder="Kategorie wählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>
                              {cat.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="text-xs text-zinc-400 space-y-0.5">
                      <div>Modul: {detectModuleFromUrl()}</div>
                      <div>Browser: {collectBrowserInfo()} · {window.innerWidth}x{window.innerHeight}</div>
                      <div>Seite: {window.location.pathname}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-1">
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !isFormValid()}
                  className={`flex-1 ${T.primaryBtn}`}
                  data-testid="feedback-submit-button"
                >
                  {isSubmitting ? 'Wird gesendet…' : 'Feedback senden'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className={lex ? 'rounded-none' : ''}
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* lex: Kanzlei-Dialog — Nachricht direkt an die betreuende Steuerberatung */}
      <Dialog open={kanzleiOpen} onOpenChange={handleKanzleiClose}>
        <DialogContent className={`sm:max-w-md ${T.radius}`} data-testid="kanzlei-contact-dialog">
          <DialogHeader>
            <DialogTitle className="tracking-tight font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Meine Steuerberatung
            </DialogTitle>
            <p className="text-sm text-zinc-500">UVA, Honorarnote, Fristen — Ihre Kanzlei antwortet persönlich.</p>
          </DialogHeader>

          <div className="space-y-4">
            <div className={`flex items-start gap-3 ${T.itemRadius} ${T.infoBox} p-3`}>
              <Landmark className={`h-4 w-4 ${T.infoIcon} mt-0.5 shrink-0`} />
              <div className="text-sm">
                <div className="text-zinc-600">Ihre Nachricht geht direkt an Ihre betreuende Steuerberatung. Antwort an:</div>
                <div className="font-semibold text-zinc-900 break-all" data-testid="kanzlei-user-email">
                  {userEmail || '…'}
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-zinc-700">Betreff (optional)</label>
              <input
                type="text"
                value={kanzleiSubject}
                onChange={(e) => setKanzleiSubject(e.target.value)}
                placeholder="z. B. Frage zur UVA Juni"
                maxLength={160}
                data-testid="kanzlei-subject-input"
                className={`w-full ${T.itemRadius} border border-zinc-200 px-3 py-2 text-sm ${T.inputFocus} focus:outline-none`}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-zinc-700">Ihr Anliegen *</label>
              <Textarea
                value={kanzleiMessage}
                onChange={(e) => setKanzleiMessage(e.target.value)}
                placeholder="Beschreiben Sie Ihr Anliegen — Ihre Steuerberatung meldet sich per E-Mail (mind. 10 Zeichen)…"
                className={`min-h-[110px] resize-none ${T.itemRadius}`}
                maxLength={5000}
                data-testid="kanzlei-message-input"
              />
            </div>

            <div className="flex gap-2 pt-1">
              <Button
                onClick={handleKanzleiSubmit}
                disabled={kanzleiSubmitting || kanzleiMessage.trim().length < 10}
                className={`flex-1 ${T.primaryBtn}`}
                data-testid="kanzlei-submit-button"
              >
                {kanzleiSubmitting ? 'Wird gesendet…' : 'An Steuerberatung senden'}
              </Button>
              <Button variant="outline" onClick={handleKanzleiClose} disabled={kanzleiSubmitting} className={lex ? 'rounded-none' : ''}>
                Abbrechen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Support Dialog – versendet via Backend/Brevo an support@ivoliq.io */}
      <Dialog open={supportOpen} onOpenChange={handleSupportClose}>
        <DialogContent className={`sm:max-w-md ${T.radius}`} data-testid="support-dialog">
          <DialogHeader>
            <DialogTitle className="tracking-tight font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Support kontaktieren
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Antwort-Adresse */}
            <div className={`flex items-start gap-3 ${T.itemRadius} ${T.infoBox} p-3`}>
              <Mail className={`h-4 w-4 ${T.infoIcon} mt-0.5 shrink-0`} />
              <div className="text-sm">
                <div className="text-zinc-600">{W.supportReplyTo}</div>
                <div className="font-semibold text-zinc-900 break-all" data-testid="support-user-email">
                  {userEmail || '…'}
                </div>
              </div>
            </div>

            {/* Betreff (optional) */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-zinc-700">Betreff (optional)</label>
              <input
                type="text"
                value={supportSubject}
                onChange={(e) => setSupportSubject(e.target.value)}
                placeholder="Worum geht es?"
                maxLength={160}
                data-testid="support-subject-input"
                className={`w-full ${T.itemRadius} border border-zinc-200 px-3 py-2 text-sm ${T.inputFocus} focus:outline-none`}
              />
            </div>

            {/* Nachricht */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-zinc-700">{W.supportMsgLabel}</label>
              <Textarea
                value={supportMessage}
                onChange={(e) => setSupportMessage(e.target.value)}
                placeholder={W.supportPlaceholder}
                className={`min-h-[110px] resize-none ${T.itemRadius}`}
                maxLength={5000}
                data-testid="support-message-input"
              />
            </div>

            {/* DSGVO-Consent */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="support-consent-checkbox"
                checked={supportConsent}
                onChange={(e) => setSupportConsent(e.target.checked)}
                data-testid="support-consent-checkbox"
                className={`mt-1 h-4 w-4 border-zinc-300 rounded ${T.checkbox}`}
              />
              <label htmlFor="support-consent-checkbox" className="text-sm text-zinc-600 cursor-pointer">
                Ich stimme zu, dass ivoliq mich zur Bearbeitung dieser Support-Anfrage
                per E-Mail kontaktiert. Details in der{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${T.link} underline`}
                >
                  Datenschutzerklärung
                </a>.
              </label>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <Button
                onClick={handleSupportSubmit}
                disabled={supportSubmitting || supportMessage.trim().length < 10 || !supportConsent}
                className={`flex-1 ${T.primaryBtn}`}
                data-testid="support-submit-button"
              >
                {supportSubmitting ? 'Wird gesendet…' : 'Anfrage senden'}
              </Button>
              <Button variant="outline" onClick={handleSupportClose} disabled={supportSubmitting} className={lex ? 'rounded-none' : ''}>
                Abbrechen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FloatingFeedbackButton;
